import {
  FC,
  useState,
  useEffect,
  useMemo,
  useCallback,
  ChangeEvent,
} from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import CloseIcon from "@mui/icons-material/Close";
import { AxiosError } from "axios";
import { UserFormTextField } from "@/components/UserFormTextField/UserFormTextField";
import { useSnackbar } from "@/components/Snackbar";
import {
  createExportQuote,
  updateExportQuote,
} from "@/apis/bacchus/exportQuote";
import {
  ExportQuote,
  ExportQuoteWriteBody,
  isValidationError,
} from "@/apis/bacchus/types";
import { formatNumber } from "@/utils/numberUtils";

export type ExportQuoteDialogMode = "create" | "edit";

const DEFAULT_CONFIG: Record<
  ExportQuoteDialogMode,
  { title: string; buttonText: string }
> = {
  create: { title: "Save Quote", buttonText: "Save Quote" },
  edit: { title: "Rename Quote", buttonText: "Save" },
};

interface ExportQuoteDialogProps {
  open: boolean;
  mode: ExportQuoteDialogMode;
  /** The quote being renamed (edit mode). */
  quote?: ExportQuote | null;
  /** Filters, settings and estimate to persist (create mode). */
  buildQuoteBody?: () => Omit<ExportQuoteWriteBody, "name">;
  /** Credits shown as a reminder of what is being saved (create mode). */
  creditsEstimate?: number | null;
  onClose: () => void;
  onSave: (quote: ExportQuote) => void;
}

function nameErrorFrom(err: unknown): string | null {
  if (!(err instanceof AxiosError) || !isValidationError(err.response?.data)) {
    return null;
  }
  const nameErrors = err.response.data.errors.name;
  if (!nameErrors?.length) return null;
  return nameErrors[0] === "has already been taken"
    ? "A quote with this name already exists"
    : nameErrors[0];
}

export const ExportQuoteDialog: FC<ExportQuoteDialogProps> = ({
  open,
  mode,
  quote,
  buildQuoteBody,
  creditsEstimate,
  onClose,
  onSave,
}) => {
  const snackbar = useSnackbar();
  const isEditMode = mode === "edit";

  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [serverNameError, setServerNameError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(isEditMode ? (quote?.name ?? "") : "");
    setNameTouched(false);
    setServerNameError(null);
  }, [open, isEditMode, quote]);

  const nameErrorMessage =
    serverNameError ?? (nameTouched && !name.trim() ? "Name is required" : "");

  const hasFormBeenModified = useMemo(
    () => !isEditMode || name.trim() !== (quote?.name ?? "").trim(),
    [isEditMode, name, quote]
  );

  const isSaveDisabled =
    isSaving || !!nameErrorMessage || !name.trim() || !hasFormBeenModified;

  const handleNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setName(e.target.value);
      setNameTouched(true);
      setServerNameError(null);
    },
    []
  );

  const handleSave = useCallback(async () => {
    setNameTouched(true);
    if (!name.trim()) return;

    setIsSaving(true);
    try {
      const saved =
        isEditMode && quote
          ? await updateExportQuote(quote.id, { name: name.trim() })
          : await createExportQuote({
              ...buildQuoteBody!(),
              name: name.trim(),
            });
      snackbar.showSuccess(
        isEditMode ? "Quote renamed successfully" : "Quote saved successfully"
      );
      onSave(saved);
    } catch (err) {
      console.error(`Failed to ${isEditMode ? "rename" : "save"} quote:`, err);
      const nameError = nameErrorFrom(err);
      if (nameError) {
        setServerNameError(nameError);
        return;
      }
      snackbar.showError(
        `Failed to ${isEditMode ? "rename" : "save"} quote. Please try again.`
      );
    } finally {
      setIsSaving(false);
    }
  }, [isEditMode, quote, name, buildQuoteBody, snackbar, onSave]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      id="export-quote-dialog"
      data-testid="export-quote-dialog"
    >
      <DialogTitle
        variant="h3"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        data-testid="export-quote-dialog-title"
      >
        {DEFAULT_CONFIG[mode].title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          data-testid="export-quote-dialog-close-btn"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3}>
          <UserFormTextField
            id="export-quote-name-input"
            inputTestId="export-quote-name-input"
            label="Name"
            value={name}
            onChange={handleNameChange}
            error={!!nameErrorMessage}
            errorMessage={nameErrorMessage}
            autoFocus={open}
            maxLength={200}
          />
          {!isEditMode && creditsEstimate != null && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Estimated credits
              </Typography>
              <Typography
                variant="body2"
                data-testid="export-quote-dialog-credits"
              >
                {formatNumber(creditsEstimate)}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          data-testid="export-quote-dialog-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isSaveDisabled}
          data-testid="export-quote-dialog-save-btn"
        >
          {isSaving ? "Saving..." : DEFAULT_CONFIG[mode].buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
